import type {
  PaymentAction,
  ResultCode,
  PaymentMethod,
  GooglePayConfiguration,
  ApplePayConfiguration,
} from '@adyen/adyen-web';
import type { ProviderEnum } from './common';
import type { AppchargeErrorType, FinalizeStatus, PayFromExpressCheckout } from './payment';
import type { Customer, Pricing } from './checkout';

export enum AdyenInputType {
  SHOPPER_EMAIL = 'shopperEmail',
  SHOPPER_NAME = 'shopperName',
  BILLING_ADDRESS = 'billingAddress',
  TELEPHONE_NUMBER = 'telephoneNumber',
  EMAIL = 'email',
  ZIP_CODE = 'zipCode',
}

export interface AdyenConfig {
  clientKey: string;
  env: 'test' | 'live';
}

export interface CardDetails {
  bin: string;
  endDigits: string;
  expiryDate: string;
  cardType?: string;
  paymentMethodData?: Record<string, unknown>;
  browserInfo?: Record<string, unknown>;
}

export interface HandlePayParams {
  email: string;
  cardholderName: string;
  shouldSaveCard: boolean;
}

export interface AdyenCheckoutInstance {
  components: {
    card?: (config: Record<string, unknown>) => AdyenCardComponent;
  };
  createFromAction: (action: unknown, config: Record<string, unknown>) => unknown;
}

export interface AdditionalDetailsData {
  data: {
    details: {
      redirectResult?: string;
      threeDSResult?: string;
      [key: string]: unknown;
    };
    paymentData?: string;
    sessionData?: string;
  };
}

export interface AdyenActionComponent {
  resolve: (data: unknown) => void;
  reject: () => void;
}

export interface CallbackActions {
  onAdditionalDetails: (state: AdditionalDetailsData, component: AdyenActionComponent) => Promise<void>;
}

export interface AdyenCardComponent {
  mount: (element: HTMLElement | string) => void;
  unmount: () => void;
  submit: () => void;
  showValidation: () => void;
  setFocusOn: (field: string) => void;
  isValid: boolean;
}

export interface AdyenUIElement {
  mount: (element: string) => void;
}

export interface CardFieldEvent {
  fieldType: string;
  valid: boolean;
  endDigits?: string;
  expiryDate?: string;
}

export interface BinValueEvent {
  binValue: string;
  brand?: string;
}

export interface AdyenFinalizePaymentResponse {
  paymentProvider: ProviderEnum.ADYEN;
  adyen: {
    message: string;
    resultCode: ResultCode;
    status: FinalizeStatus;
    action?: PaymentAction;
    redirectUrl?: string;
  };
  orderId: string;
  appChargeErrorType?: AppchargeErrorType;
}

export interface AdyenGooglePayHandlers {
  onClick?: GooglePayConfiguration['onClick'];
  onAuthorized?: GooglePayConfiguration['onAuthorized'];
  onError?: GooglePayConfiguration['onError'];
  onPaymentCompleted?: GooglePayConfiguration['onPaymentCompleted'];
  onPaymentFailed?: GooglePayConfiguration['onPaymentFailed'];
  onSubmit?: GooglePayConfiguration['onSubmit'];
}

export interface CreateGooglePayComponentOptions {
  configuration: { gatewayMerchantId: string } & Record<string, unknown>;
  transactionInfo: {
    displayItems: unknown[];
    countryCode: string;
    currencyCode: string;
    totalPriceStatus: 'FINAL' | 'ESTIMATED' | 'NOT_CURRENTLY_KNOWN';
    totalPrice: string;
    totalPriceLabel: string;
  };
  handlers: AdyenGooglePayHandlers;
}

export interface AdyenGooglePayElementOptionsParams {
  gatewayMerchantId: string;
  googlePayConfiguration: Record<string, unknown>;
  submitParamsRef: { current: { email: string } };
  customer: Customer | null;
  handleError: (err?: unknown) => void;
  isPaymentInProgress: { current: boolean };
  runPayment: (
    params?: unknown,
    payFromExpressCheckout?: PayFromExpressCheckout,
    provider?: ProviderEnum
  ) => Promise<unknown>;
  pricing: Pricing;
}

export interface CreateGooglePayComponentParams {
  googlePayPaymentMethod: {
    publicConfig?: unknown;
    paymentMethodConfig?: { configuration?: Record<string, unknown> };
  };
  customer: Customer | null;
  pricing: Pricing;
  handlers: AdyenGooglePayHandlers;
}

export interface AdyenGooglePayConfig {
  clientKey: string;
  environment?: 'test' | 'live';
  env?: string;
  merchantId?: string;
}

export interface AdyenGooglePayPaymentMethod extends Omit<PaymentMethod, 'publicConfig'> {
  publicConfig?: AdyenGooglePayConfig;
  paymentMethodConfig?: { type?: string; configuration?: Record<string, unknown> };
}

export interface AdyenApplePayHandlers {
  onClick?: ApplePayConfiguration['onClick'];
  onAuthorized?: ApplePayConfiguration['onAuthorized'];
  onError?: ApplePayConfiguration['onError'];
  onPaymentCompleted?: ApplePayConfiguration['onPaymentCompleted'];
  onPaymentFailed?: ApplePayConfiguration['onPaymentFailed'];
  onSubmit?: ApplePayConfiguration['onSubmit'];
}

export interface AdyenApplePayConfig {
  clientKey: string;
  environment?: 'test' | 'live';
  env?: string;
}

export interface AdyenApplePayPaymentMethod extends Omit<PaymentMethod, 'publicConfig'> {
  publicConfig?: AdyenApplePayConfig;
  paymentMethodConfig?: { type?: string; configuration?: Record<string, unknown> };
}

export interface CreateApplePayComponentParams {
  applePayPaymentMethod: {
    publicConfig?: unknown;
    paymentMethodConfig?: { configuration?: Record<string, unknown> };
  };
  pricing: Pricing;
  handlers: AdyenApplePayHandlers;
}

export interface ApplePayPaymentDetailsRef {
  email: string;
  paymentMethodDetails: Record<string, unknown>;
}
